// Portfolio Administration v1.0 PR 6 — filter row for the cross-portfolio
// Loan Tape page. Period selector + variance toggle + days-late preset +
// free-text search.

import { Input, Label } from "@/design-system/components/ui";

interface LoanTapeFiltersProps {
  period: string;
  recentPeriods: string[];
  variance: boolean | undefined;
  minDaysLate: number | undefined;
  search: string;
  onPeriodChange: (p: string) => void;
  onVarianceChange: (v: boolean | undefined) => void;
  onMinDaysLateChange: (n: number | undefined) => void;
  onSearchChange: (s: string) => void;
}

const DAYS_LATE_PRESETS: { label: string; value: number | undefined }[] = [
  { label: "Any", value: undefined },
  { label: "1-29", value: 1 },
  { label: "30-59", value: 30 },
  { label: "60+", value: 60 },
];

export function LoanTapeFilters({
  period,
  recentPeriods,
  variance,
  minDaysLate,
  search,
  onPeriodChange,
  onVarianceChange,
  onMinDaysLateChange,
  onSearchChange,
}: LoanTapeFiltersProps) {
  return (
    <div
      className="flex flex-wrap items-end gap-space-3"
      data-testid="loan-tape-filters"
    >
      <div className="flex flex-col gap-space-1">
        <Label htmlFor="loan-tape-period">Period</Label>
        <select
          id="loan-tape-period"
          value={period}
          onChange={(e) => onPeriodChange(e.target.value)}
          className="h-10 border border-border-light rounded-md px-space-2 text-small bg-white-pure"
          data-testid="loan-tape-period-selector"
        >
          {recentPeriods.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-space-1">
        <Label htmlFor="loan-tape-variance">Variance</Label>
        <select
          id="loan-tape-variance"
          value={variance === undefined ? "any" : variance ? "yes" : "no"}
          onChange={(e) => {
            const v = e.target.value;
            onVarianceChange(
              v === "any" ? undefined : v === "yes",
            );
          }}
          className="h-10 border border-border-light rounded-md px-space-2 text-small bg-white-pure"
          data-testid="loan-tape-variance-filter"
        >
          <option value="any">Any</option>
          <option value="yes">Variance flagged</option>
          <option value="no">No variance</option>
        </select>
      </div>
      <div className="flex flex-col gap-space-1">
        <Label htmlFor="loan-tape-days-late">Days late</Label>
        <select
          id="loan-tape-days-late"
          value={minDaysLate ?? ""}
          onChange={(e) =>
            onMinDaysLateChange(
              e.target.value === "" ? undefined : Number(e.target.value),
            )
          }
          className="h-10 border border-border-light rounded-md px-space-2 text-small bg-white-pure"
          data-testid="loan-tape-days-late-filter"
        >
          {DAYS_LATE_PRESETS.map((p) => (
            <option key={p.label} value={p.value ?? ""}>{p.label}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-space-1 flex-1 min-w-[180px]">
        <Label htmlFor="loan-tape-search">Search</Label>
        <Input
          id="loan-tape-search"
          value={search}
          placeholder="Loan ID / servicer reference"
          onChange={(e) => onSearchChange(e.target.value)}
          data-testid="loan-tape-search"
        />
      </div>
    </div>
  );
}
